/**
 * Every GSTIN a business holds, gathered from the two places Karr keeps them:
 * its GST registrations (Settings, Registrations: one per state) and its places
 * of business, which may carry a GSTIN of their own (`branches.gstin`).
 *
 * The org list reads both at sign-in, in the same request that brings each
 * business's name (`useAuth`), and Settings hands back a fresh copy after any
 * save. So the switcher's G¹ G² G³ cost no call of their own and are never read
 * on a click (orgidfast: the founder found the switcher slower after #2702,
 * which read them when it opened).
 *
 * Kept apart from `orgIds.ts` on purpose: `useAuth` is in the first load, and
 * this is all it needs.
 *
 * @since orgidfast (2026-10-10)
 */

// The 14th place is usually Z, but a TCS collector's GSTIN carries C there.
export const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][0-9A-Z]{3}$/;

interface RegRow { identifier?: string | null; kind?: string | null; status?: string | null }
interface PlaceRow { gstin?: string | null; is_active?: boolean | null }

/**
 * The business's GSTINs, each once, in order. A registration counts when it is
 * GST and active (rows a caller already filtered carry neither field); a place
 * counts unless it is archived. Anything not shaped like a GSTIN is left out.
 *
 * Returns undefined when neither list was read, so "not asked" is never
 * mistaken for "has none".
 */
export function gstinsOf(regs: unknown, places: unknown): string[] | undefined {
    if (!Array.isArray(regs) && !Array.isArray(places)) return undefined;
    const out = new Set<string>();
    for (const r of (Array.isArray(regs) ? regs : []) as RegRow[]) {
        if ((r.kind ?? 'gst') !== 'gst' || (r.status ?? 'active') !== 'active') continue;
        const g = (r.identifier ?? '').trim().toUpperCase();
        if (GSTIN_RE.test(g)) out.add(g);
    }
    for (const p of (Array.isArray(places) ? places : []) as PlaceRow[]) {
        if (p.is_active === false) continue;
        const g = (p.gstin ?? '').trim().toUpperCase();
        if (GSTIN_RE.test(g)) out.add(g);
    }
    return [...out].sort();
}
